import React, { useState, useRef } from "react";
import { saveAs } from "file-saver";

const FlowCreator = ({ onBackToOptionSelector, authData }) => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState([]);
  const [lucidchartData, setLucidchartData] = useState(null);
  const [wxccFlowData, setWxccFlowData] = useState(null);
  const [conversionResults, setConversionResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const logContainerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Helper function for logging progress
  const updateProgress = (type, message) => {
    setProgress((prev) => {
      const newProgress = [
        ...prev,
        { type, message, timestamp: new Date().toLocaleTimeString() },
      ];
      return newProgress;
    });

    // Scroll to bottom after state update
    setTimeout(() => {
      if (logContainerRef.current) {
        logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
      }
    }, 0);
  };

  // Styles for progress messages
  const getMessageStyle = (type) => {
    switch (type) {
      case "info":
        return { color: "#2196f3" };
      case "success":
        return { color: "#4caf50" };
      case "warning":
        return { color: "#ff9800" };
      case "error":
        return { color: "#f44336" };
      default:
        return { color: "#333" };
    }
  };

  // Lucidchart JSON Structure Analysis
  const analyzeLucidchartStructure = (data) => {
    updateProgress("info", "Analyzing Lucidchart JSON structure...");

    const analysis = {
      pages: 0,
      shapes: 0,
      connectors: 0,
      textBoxes: 0,
      startNodes: 0,
      endNodes: 0,
      decisionNodes: 0,
      processNodes: 0,
      unknownNodes: 0
    };

    try {
      // Common Lucidchart structure analysis
      if (data.pages && Array.isArray(data.pages)) {
        analysis.pages = data.pages.length;

        data.pages.forEach(page => {
          if (page.objects && Array.isArray(page.objects)) {
            page.objects.forEach(obj => {
              // Analyze different object types
              if (obj.class === 'Shape' || obj.type === 'shape') {
                analysis.shapes++;

                // Try to classify by shape type for flow mapping
                const shapeType = obj.shapeType || obj.shape || obj.libraryItemId;
                const label = obj.text || obj.textAreas?.[0]?.text || '';

                if (shapeType) {
                  if (shapeType.includes('start') || shapeType.includes('Begin') || label.toLowerCase().includes('start')) {
                    analysis.startNodes++;
                  } else if (shapeType.includes('end') || shapeType.includes('End') || label.toLowerCase().includes('end')) {
                    analysis.endNodes++;
                  } else if (shapeType.includes('decision') || shapeType.includes('Diamond') || shapeType.includes('rhombus')) {
                    analysis.decisionNodes++;
                  } else if (shapeType.includes('process') || shapeType.includes('Rectangle')) {
                    analysis.processNodes++;
                  } else {
                    analysis.unknownNodes++;
                  }
                }
              } else if (obj.class === 'Line' || obj.type === 'line' || obj.class === 'Connector') {
                analysis.connectors++;
              } else if (obj.class === 'Text' || obj.type === 'text') {
                analysis.textBoxes++;
              }
            });
          }
        });
      }

      updateProgress("success", `Lucidchart Analysis Complete: ${analysis.pages} pages, ${analysis.shapes} shapes, ${analysis.connectors} connectors`);
      return analysis;

    } catch (error) {
      updateProgress("error", `Error analyzing Lucidchart structure: ${error.message}`);
      return analysis;
    }
  };

  // WxCC Flow Node Types and Structure
  const WXCC_NODE_TYPES = {
    START: 'startEvent',
    END: 'endEvent',
    MENU: 'menu',
    PLAY_MESSAGE: 'playMessage',
    QUEUE_CONTACT: 'queueContact',
    CONDITION: 'condition',
    SET_VARIABLE: 'setVariable',
    HTTP_REQUEST: 'httpRequest',
    PARSE_XML: 'parseXml',
    DISCONNECT: 'disconnect',
    TRANSFER: 'transfer'
  };

  // Enhanced shape type mapping with more specific detection
  const SHAPE_TYPE_MAPPING = {
    // Start/End shapes - multiple variations
    'start': WXCC_NODE_TYPES.START,
    'begin': WXCC_NODE_TYPES.START,
    'oval': WXCC_NODE_TYPES.START,
    'ellipse': WXCC_NODE_TYPES.START,
    'startpoint': WXCC_NODE_TYPES.START,
    'circle': WXCC_NODE_TYPES.START, // Could also be menu
    'end': WXCC_NODE_TYPES.END,
    'terminate': WXCC_NODE_TYPES.END,
    'endpoint': WXCC_NODE_TYPES.END,
    'finish': WXCC_NODE_TYPES.END,

    // Process shapes - various rectangle types
    'rectangle': WXCC_NODE_TYPES.PLAY_MESSAGE,
    'process': WXCC_NODE_TYPES.PLAY_MESSAGE,
    'square': WXCC_NODE_TYPES.PLAY_MESSAGE,
    'rounded_rectangle': WXCC_NODE_TYPES.PLAY_MESSAGE,
    'box': WXCC_NODE_TYPES.PLAY_MESSAGE,

    // Decision shapes - diamond variations
    'diamond': WXCC_NODE_TYPES.CONDITION,
    'decision': WXCC_NODE_TYPES.CONDITION,
    'rhombus': WXCC_NODE_TYPES.CONDITION,
    'conditional': WXCC_NODE_TYPES.CONDITION,

    // Queue and Menu shapes
    'queue': WXCC_NODE_TYPES.QUEUE_CONTACT,
    'menu': WXCC_NODE_TYPES.MENU,
    'choice': WXCC_NODE_TYPES.MENU,
    'options': WXCC_NODE_TYPES.MENU,

    // Special WxCC-specific shapes
    'transfer': WXCC_NODE_TYPES.TRANSFER,
    'disconnect': WXCC_NODE_TYPES.DISCONNECT,
    'http': WXCC_NODE_TYPES.HTTP_REQUEST,
    'api': WXCC_NODE_TYPES.HTTP_REQUEST,
    'variable': WXCC_NODE_TYPES.SET_VARIABLE,
    'setvariable': WXCC_NODE_TYPES.SET_VARIABLE
  };

  // Advanced node detection based on text content
  const TEXT_BASED_DETECTION = {
    // Start indicators
    start: [/^start$/i, /begin/i, /entry/i, /incoming/i],

    // End indicators
    end: [/^end$/i, /terminate/i, /finish/i, /complete/i, /disconnect/i, /hangup/i],

    // Menu indicators
    menu: [/menu/i, /press.*\d/i, /option/i, /select/i, /choose/i, /ivr/i],

    // Queue indicators
    queue: [/queue.*to/i, /agent/i, /skill/i, /wait/i, /hold/i, /team.*queue/i],

    // Condition indicators
    condition: [/if/i, /check/i, /validate/i, /compare/i, /\?/],

    // Message indicators
    message: [/play/i, /say/i, /announce/i, /message/i, /prompt/i, /tts/i, /text.*speech/i, /welcome/i, /thank/i],

    // Transfer indicators
    transfer: [/transfer/i, /forward/i, /route/i, /redirect/i],

    // HTTP/API indicators
    http: [/http/i, /api/i, /rest/i, /web.*service/i, /call.*api/i],

    // Variable indicators
    variable: [/set.*variable/i, /variable/i, /store/i, /save.*data/i]
  };

  // Enhanced node type detection function
  const detectNodeType = (shapeType, label, shape) => {
    const lowerShapeType = shapeType.toLowerCase();
    const lowerLabel = label.toLowerCase();

    // 1. First try text-based detection (highest priority)
    for (const [nodeTypeKey, regexArray] of Object.entries(TEXT_BASED_DETECTION)) {
      for (const regex of regexArray) {
        if (regex.test(lowerLabel)) {
          switch (nodeTypeKey) {
            case 'start': return WXCC_NODE_TYPES.START;
            case 'end': return WXCC_NODE_TYPES.END;
            case 'menu': return WXCC_NODE_TYPES.MENU;
            case 'queue': return WXCC_NODE_TYPES.QUEUE_CONTACT;
            case 'condition': return WXCC_NODE_TYPES.CONDITION;
            case 'message': return WXCC_NODE_TYPES.PLAY_MESSAGE;
            case 'transfer': return WXCC_NODE_TYPES.TRANSFER;
            case 'http': return WXCC_NODE_TYPES.HTTP_REQUEST;
            case 'variable': return WXCC_NODE_TYPES.SET_VARIABLE;
            default: break;
          }
        }
      }
    }

    // 2. Then try direct shape type mapping
    for (const [lucidType, wxccType] of Object.entries(SHAPE_TYPE_MAPPING)) {
      if (lowerShapeType.includes(lucidType)) {
        return wxccType;
      }
    }

    // 3. Finally, use shape-based heuristics with improved logic
    if (lowerShapeType.includes('diamond') || lowerShapeType.includes('rhombus')) {
      return WXCC_NODE_TYPES.CONDITION;
    } else if (lowerShapeType.includes('oval') || lowerShapeType.includes('ellipse')) {
      // Improved end detection
      if (lowerLabel.includes('end') || lowerLabel.includes('finish') ||
          lowerLabel.includes('terminate') || (lowerLabel.includes('call') && lowerLabel.includes('end'))) {
        return WXCC_NODE_TYPES.END;
      }
      return WXCC_NODE_TYPES.START;
    } else if (lowerShapeType.includes('circle')) {
      // Improved circle detection
      if (lowerLabel.includes('menu') || lowerLabel.includes('press')) {
        return WXCC_NODE_TYPES.MENU;
      } else if (lowerLabel.includes('queue') || lowerLabel.includes('agent') ||
                 lowerLabel.includes('team') || lowerLabel.includes('skill')) {
        return WXCC_NODE_TYPES.QUEUE_CONTACT;
      }
      return WXCC_NODE_TYPES.START;
    } else if (lowerShapeType.includes('rectangle') || lowerShapeType.includes('square')) {
      // Improved rectangle detection
      if (lowerLabel.includes('queue') || lowerLabel.includes('agent') ||
          lowerLabel.includes('team') || lowerLabel.includes('skill')) {
        return WXCC_NODE_TYPES.QUEUE_CONTACT;
      }
      return WXCC_NODE_TYPES.PLAY_MESSAGE;
    }

    // Default fallback
    return WXCC_NODE_TYPES.PLAY_MESSAGE;
  };

  // Convert Lucidchart shapes to WxCC activities
  const convertShapeToWxccActivity = (shape, index) => {
    const shapeType = (shape.shapeType || shape.shape || shape.libraryItemId || '').toLowerCase();
    const label = shape.text || shape.textAreas?.[0]?.text || `Activity_${index}`;

    // Use enhanced detection
    const nodeType = detectNodeType(shapeType, label, shape);

    // Generate unique activity ID
    const activityId = `activity_${shape.id || index}`;
    const activityName = label || `Activity ${index + 1}`;

    // Minimal WxCC activity structure
    const wxccActivity = {
      "activityId": activityId,
      "name": activityName.slice(0, 50),
      "activityType": nodeType,
      "transitions": [],
      "properties": {}
    };

    // Minimal activity configuration
    wxccActivity.activityType = nodeType;

    // Set basic properties based on type
    switch (nodeType) {
      case WXCC_NODE_TYPES.PLAY_MESSAGE:
        wxccActivity.properties = { "textToSpeech": label };
        break;
      case WXCC_NODE_TYPES.MENU:
        wxccActivity.properties = { "textToSpeech": "Please make a selection" };
        break;
      case WXCC_NODE_TYPES.CONDITION:
        wxccActivity.properties = { "condition": "true" };
        break;
      case WXCC_NODE_TYPES.QUEUE_CONTACT:
        wxccActivity.properties = { "priority": 1 };
        break;
      default:
        wxccActivity.properties = {};
        break;
    }

    return wxccActivity;
  };

  // Parse connectors for WxCC activities
  const parseConnectorsForActivities = (connectors, activityMap, wxccFlow) => {
    updateProgress("info", "Parsing connectors and transitions...");

    connectors.forEach(connector => {
      const fromId = connector.endpoint1?.id || connector.from;
      const toId = connector.endpoint2?.id || connector.to;
      const label = connector.text || connector.label || '';

      if (fromId && toId && activityMap[fromId] && activityMap[toId]) {
        const fromActivity = activityMap[fromId];
        const toActivityId = activityMap[toId].activityId;

        // Update transitions based on activity type and connector label
        if (fromActivity.activityType === 'condition') {
          // Handle condition transitions
          const trueTransition = fromActivity.transitions.find(t => t.condition === 'true');
          const falseTransition = fromActivity.transitions.find(t => t.condition === 'false');

          if (label.toLowerCase().includes('yes') || label.toLowerCase().includes('true')) {
            if (trueTransition) trueTransition.targetActivityId = toActivityId;
          } else if (label.toLowerCase().includes('no') || label.toLowerCase().includes('false')) {
            if (falseTransition) falseTransition.targetActivityId = toActivityId;
          } else {
            // Default to true path if unclear
            if (trueTransition) trueTransition.targetActivityId = toActivityId;
          }
        } else if (fromActivity.activityType === 'menu') {
          // Handle menu transitions
          const digitMatch = label.match(/(\d)/);
          if (digitMatch) {
            const digit = digitMatch[1];
            // Add to menu options
            if (!fromActivity.properties.menuOptions) {
              fromActivity.properties.menuOptions = [];
            }
            fromActivity.properties.menuOptions.push({
              digit: digit,
              targetActivityId: toActivityId,
              description: `Option ${digit}`
            });
          } else {
            // Default transition
            const defaultTransition = fromActivity.transitions.find(t => t.condition === 'default');
            if (defaultTransition) defaultTransition.targetActivityId = toActivityId;
          }
        } else {
          // Handle standard transitions (success/error)
          const successTransition = fromActivity.transitions.find(t => t.condition === 'success');
          const errorTransition = fromActivity.transitions.find(t => t.condition === 'error');

          if (label.toLowerCase().includes('error') || label.toLowerCase().includes('fail')) {
            if (errorTransition) errorTransition.targetActivityId = toActivityId;
          } else {
            // Default to success path
            if (successTransition) {
              successTransition.targetActivityId = toActivityId;
            } else if (fromActivity.transitions.length > 0) {
              // Use first available transition
              fromActivity.transitions[0].targetActivityId = toActivityId;
            }
          }
        }
      }
    });
  };

  // Main conversion function
  const convertLucidchartToWxcc = (lucidchartData) => {
    updateProgress("info", "Starting Lucidchart to WxCC Flow conversion...");

    try {
      // Create WxCC flow structure based on actual format
      const flowName = (lucidchartData.title && lucidchartData.title.trim()) || "Converted Lucidchart Flow";
      updateProgress("info", `Creating flow: "${flowName}"`);

      // Simplest possible WxCC flow structure
      const wxccFlow = {
        "version": "v1.0",
        "name": flowName,
        "description": lucidchartData.description || "Converted from Lucidchart diagram",
        "type": "INBOUND",
        "startActivity": null,
        "activities": [],
        "globalVariables": []
      };

      const activityMap = {};
      const connectors = [];
      let activityIndex = 0;
      let startActivityId = null;

      // Process all pages
      if (lucidchartData.pages && Array.isArray(lucidchartData.pages)) {
        lucidchartData.pages.forEach((page, pageIndex) => {
          updateProgress("info", `Processing page ${pageIndex + 1}...`);

          if (page.objects && Array.isArray(page.objects)) {
            page.objects.forEach(obj => {
              if (obj.class === 'Shape' || obj.type === 'shape') {
                const wxccActivity = convertShapeToWxccActivity(obj, activityIndex++);
                wxccFlow.activities.push(wxccActivity);
                activityMap[obj.id] = wxccActivity;

                // Mark start activity (only capture the first one found)
                if (wxccActivity.activityType === 'startEvent' && !startActivityId) {
                  startActivityId = wxccActivity.activityId;
                }
              } else if (obj.class === 'Line' || obj.type === 'line' || obj.class === 'Connector') {
                connectors.push(obj);
              }
            });
          }
        });
      }

      // Set the start activity
      if (startActivityId) {
        wxccFlow.startActivity = startActivityId;
      } else if (wxccFlow.activities.length > 0) {
        // If no explicit start found, use first activity
        wxccFlow.startActivity = wxccFlow.activities[0].activityId;
      }

      // Process connectors to create transitions
      parseConnectorsForActivities(connectors, activityMap, wxccFlow);

      updateProgress("success", `Conversion complete: "${flowName}" with ${wxccFlow.activities.length} activities created`);
      return wxccFlow;

    } catch (error) {
      updateProgress("error", `Conversion failed: ${error.message}`);
      throw error;
    }
  };

  // File handling
  const handleFileUpload = async (file) => {
    if (!file) return;

    updateProgress("info", `Processing file: ${file.name}`);
    setLoading(true);

    try {
      const text = await file.text();
      const jsonData = JSON.parse(text);

      setLucidchartData(jsonData);
      updateProgress("success", "Lucidchart file loaded successfully");

      // Analyze structure
      const analysis = analyzeLucidchartStructure(jsonData);
      setConversionResults(analysis);

    } catch (error) {
      updateProgress("error", `Failed to load file: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Deploy flow to WxCC (requires authentication)
  const handleDeployToWxCC = async () => {
    if (!wxccFlowData) {
      updateProgress("error", "Please convert a flow first");
      return;
    }

    if (!authData || !authData.access_token) {
      updateProgress("error", "Authentication required for flow deployment");
      return;
    }

    setLoading(true);

    try {
      updateProgress("info", "Deploying flow to WebEx Contact Center...");

      // WxCC Flow Deployment API
      const deployResponse = await fetch(`https://webexapis.com/v1/contactCenter/flows`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authData.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: wxccFlowData.name,
          description: wxccFlowData.description,
          type: 'inbound',
          tags: ['converted-from-lucidchart'],
          flowJson: wxccFlowData
        })
      });

      if (deployResponse.ok) {
        const deployResult = await deployResponse.json();
        updateProgress("success", `Flow deployed successfully! Flow ID: ${deployResult.id}`);
        setConversionResults(prev => ({ ...prev, deployedFlowId: deployResult.id }));
      } else {
        const error = await deployResponse.json();
        updateProgress("error", `Deployment failed: ${error.message || deployResponse.statusText}`);
      }

    } catch (error) {
      updateProgress("error", `Deployment error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Convert and export
  const handleConvert = async () => {
    if (!lucidchartData) {
      updateProgress("error", "Please upload a Lucidchart file first");
      return;
    }

    setLoading(true);

    try {
      const wxccFlow = convertLucidchartToWxcc(lucidchartData);
      setWxccFlowData(wxccFlow);

      // Export as JSON file
      const blob = new Blob([JSON.stringify(wxccFlow, null, 2)], {
        type: 'application/json'
      });

      const fileName = `wxcc_flow_${new Date().toISOString().slice(0, 10)}.json`;
      saveAs(blob, fileName);

      updateProgress("success", `WxCC Flow exported as ${fileName}`);

    } catch (error) {
      updateProgress("error", `Conversion failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const jsonFile = files.find(file =>
      file.type === 'application/json' || file.name.endsWith('.json')
    );

    if (jsonFile) {
      handleFileUpload(jsonFile);
    } else {
      updateProgress("error", "Please drop a JSON file");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  return (
    <div style={{
      padding: '20px',
      maxWidth: '1200px',
      margin: 'auto',
      fontFamily: 'Inter, sans-serif',
      backgroundColor: '#f9f9f9',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    }}>
      <h1 style={{ color: '#333', textAlign: 'center', marginBottom: '25px' }}>
        Flow Creator - Lucidchart to WxCC Converter
      </h1>

      <p style={{ textAlign: 'center', color: '#555', marginBottom: '30px' }}>
        Upload a Lucidchart JSON export and convert it to a WebEx Contact Center Flow
      </p>

      {/* Authentication Status */}
      {authData && authData.access_token ? (
        <div style={{
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          padding: '10px',
          margin: '20px 0',
          textAlign: 'center'
        }}>
          ✅ Authenticated - Flow deployment enabled
        </div>
      ) : (
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeeba',
          borderRadius: '4px',
          padding: '10px',
          margin: '20px 0',
          textAlign: 'center',
          color: '#856404'
        }}>
          ⚠️ No authentication - Export only mode
        </div>
      )}

      {/* File Upload Area */}
      <div
        style={{
          border: dragOver ? '2px dashed #007bff' : '2px dashed #ccc',
          borderRadius: '8px',
          padding: '40px',
          textAlign: 'center',
          marginBottom: '30px',
          backgroundColor: dragOver ? '#f0f8ff' : '#fff',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        <div style={{ fontSize: '48px', color: '#ccc', marginBottom: '15px' }}>
          📄
        </div>

        <h3 style={{ color: '#333', marginBottom: '10px' }}>
          Drop Lucidchart JSON file here
        </h3>

        <p style={{ color: '#666', marginBottom: '20px' }}>
          or click to browse and select a file
        </p>

        {lucidchartData && (
          <div style={{
            backgroundColor: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            padding: '10px',
            margin: '20px 0'
          }}>
            ✅ Lucidchart file loaded successfully
          </div>
        )}
      </div>

      {/* Conversion Results */}
      {conversionResults && (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '30px'
        }}>
          <h3 style={{ color: '#333', marginBottom: '15px' }}>Analysis Results</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
            <div><strong>Pages:</strong> {conversionResults.pages}</div>
            <div><strong>Shapes:</strong> {conversionResults.shapes}</div>
            <div><strong>Connectors:</strong> {conversionResults.connectors}</div>
            <div><strong>Start Nodes:</strong> {conversionResults.startNodes}</div>
            <div><strong>End Nodes:</strong> {conversionResults.endNodes}</div>
            <div><strong>Decision Nodes:</strong> {conversionResults.decisionNodes}</div>
            <div><strong>Process Nodes:</strong> {conversionResults.processNodes}</div>
            <div><strong>Unknown Nodes:</strong> {conversionResults.unknownNodes}</div>
          </div>
        </div>
      )}

      {/* Control Buttons */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
        <button
          onClick={handleConvert}
          disabled={loading || !lucidchartData}
          style={{
            padding: '12px 25px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: (loading || !lucidchartData) ? 'not-allowed' : 'pointer',
            opacity: (loading || !lucidchartData) ? 0.7 : 1,
            fontSize: '1em',
            fontWeight: '600',
            transition: 'background-color 0.3s ease, opacity 0.3s ease',
            boxShadow: '0 2px 8px rgba(0, 123, 255, 0.2)'
          }}
        >
          {loading ? "Converting..." : "Convert & Export Flow"}
        </button>

        {/* Deploy Button - only show if authenticated and flow exists */}
        {authData && authData.access_token && wxccFlowData && (
          <button
            onClick={handleDeployToWxCC}
            disabled={loading}
            style={{
              padding: '12px 25px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              fontSize: '1em',
              fontWeight: '600',
              transition: 'background-color 0.3s ease, opacity 0.3s ease',
              boxShadow: '0 2px 8px rgba(40, 167, 69, 0.2)'
            }}
          >
            {loading ? "Deploying..." : "Deploy to WxCC"}
          </button>
        )}
      </div>

      {/* Progress Log */}
      <div style={{ borderTop: "1px solid #eee", paddingTop: "20px", marginTop: "20px" }}>
        <h2 style={{ color: "#333", marginBottom: '15px' }}>Conversion Log:</h2>
        <div
          ref={logContainerRef}
          style={{
            backgroundColor: '#e9ecef',
            padding: '15px',
            borderRadius: '6px',
            maxHeight: '400px',
            overflowY: 'auto',
            border: '1px solid #dee2e6',
            marginBottom: '20px'
          }}
        >
          <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
            {progress.map((item, index) => (
              <li
                key={index}
                style={{
                  marginBottom: "8px",
                  fontSize: "0.9em",
                  ...getMessageStyle(item.type),
                }}
              >
                <span style={{ fontWeight: "normal", color: "#666" }}>
                  [{item.timestamp}]{" "}
                </span>
                {item.message}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* WxCC Flow Preview */}
      {wxccFlowData && (
        <div style={{ borderTop: "1px solid #eee", paddingTop: "20px", marginTop: "20px" }}>
          <h2 style={{ color: "#333", marginBottom: '15px' }}>Generated WxCC Flow Preview:</h2>
          <pre style={{
            backgroundColor: '#f8f9fa',
            padding: '15px',
            borderRadius: '6px',
            maxHeight: '400px',
            overflowY: 'auto',
            border: '1px solid #dee2e6',
            fontSize: '0.8em',
            lineHeight: '1.4'
          }}>
            {JSON.stringify(wxccFlowData, null, 2)}
          </pre>
        </div>
      )}

      {/* Back Button */}
      <div style={{ textAlign: 'center', marginTop: '30px' }}>
        <button
          onClick={onBackToOptionSelector}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6c757d',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '0.9em',
            transition: 'background-color 0.3s ease'
          }}
        >
          Back to Option Selector
        </button>
      </div>
    </div>
  );
};

export default FlowCreator;